import { Button } from "@/components/ui/button";
import { Document } from "@/graphql/__generated__/graphql";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CircleLoader } from "../loaders";
import { cn } from "@/lib/utils";

function TextRenderComponent({ selectedDocument, customHeightClassName }: { selectedDocument: Document, customHeightClassName?: string }) {
  const { t } = useTranslation(["translation", "common"]);
  const [content, setContent] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const response = await fetch(selectedDocument.url);
        if (!response.ok) {
          throw new Error(t("edittender.documents.unabletofetchdocument"));
        }
        const text = await response.text();
        setContent(text);
      } catch (err) {
        setError(err instanceof Error ? err.message : t("edittender.documents.anerroroccurred"));
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [selectedDocument.url]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <CircleLoader
          visible={true}
          height={80}
          width={80}
          strokeWidth={8}
          ariaLabel="oval-loading"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 flex flex-col gap-4">
        <div className="text-sm text-red-500 bg-red-50 p-4 rounded-md border border-red-200">
          <span className="font-medium mr-1">
            {t("edittender.documents.unabletoloaddocument")}:
          </span>
          {error}
        </div>
        <Button
          onClick={() => window.open(selectedDocument.url, "_blank")}
          variant="outline"
        >
          {t("common:buttons.download")}
        </Button>
      </div>
    );
  }

  return (
    <div className={cn("p-4 whitespace-pre-wrap overflow-y-auto", customHeightClassName ?? "max-h-[calc(100vh-150px)]")}>
      {content}
    </div>
  );
}

export default TextRenderComponent;
